import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search,
  Eye,
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Plus,
  Filter,
  Grid,
  List,
  Mail,
  Phone,
  Heart,
  Utensils,
  Accessibility,
  Star,
  ExternalLink,
  Copy,
  Check,
  ShoppingBag,
  Link2,
  Camera,
  Trash2,
  Upload,
  X,
  Image,
  RefreshCw,
  Send,
  Edit3,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ui/image-upload";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("session_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

/* ── Host Inspo Section ── */
const HostInspoSection = ({ clientUserId, clientName }) => {
  const [inspoItems, setInspoItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newFolder, setNewFolder] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (clientUserId) fetchInspo();
  }, [clientUserId]);

  const fetchInspo = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/host/clients/${clientUserId}/inspo`, { headers: getHeaders() });
      const data = await res.json();
      setInspoItems(Array.isArray(data) ? data : data?.items || []);
    } catch { setInspoItems([]); }
    finally { setLoading(false); }
  };

  const addInspo = async () => {
    if (!newUrl.trim()) return;
    setUploading(true);
    try {
      const res = await fetch(`${API}/host/clients/${clientUserId}/inspo`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ image_url: newUrl.trim(), folder: newFolder.trim() || "general", source: "host_upload" }),
      });
      if (res.ok) {
        toast.success("Inspo added for " + clientName);
        setNewUrl("");
        setShowAdd(false);
        fetchInspo();
      } else toast.error("Could not add inspo");
    } catch { toast.error("Error adding inspo"); }
    finally { setUploading(false); }
  };

  const deleteInspo = async (inspoId) => {
    try {
      const res = await fetch(`${API}/host/clients/${clientUserId}/inspo/${inspoId}`, {
        method: "DELETE", headers: getHeaders(),
      });
      if (res.ok) { toast.success("Removed"); fetchInspo(); }
      else toast.error("Could not remove");
    } catch { toast.error("Error removing inspo"); }
  };

  if (loading) return <div className="py-3 text-xs text-gray-400 text-center">Loading inspo...</div>;

  return (
    <div className="border-t border-gray-100 pt-3" data-testid="host-inspo-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          <Camera className="w-3.5 h-3.5" /> Private Inspo for {clientName}
        </h4>
        <Button variant="ghost" size="sm" onClick={() => setShowAdd(!showAdd)} className="h-7 text-xs gap-1">
          {showAdd ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          {showAdd ? "Cancel" : "Add"}
        </Button>
      </div>

      {/* Add inspo form */}
      {showAdd && (
        <div className="space-y-2 mb-3" data-testid="add-inspo-form">
          <ImageUpload
            label="Inspo image"
            value={newUrl}
            onChange={(url) => setNewUrl(url || "")}
            previewSize="medium"
            aspectRatio="square"
            description="Upload from your device. No URL pasting; this is a real upload."
          />
          <div className="flex gap-2">
            <Input value={newFolder} onChange={e => setNewFolder(e.target.value)}
              placeholder="Folder (optional)" className="h-8 text-xs flex-1" />
            <Button onClick={addInspo} disabled={uploading || !newUrl.trim()} size="sm" className="h-8 text-xs">
              {uploading ? "Saving..." : "Save inspo"}
            </Button>
          </div>
        </div>
      )}

      {/* Inspo grid */}
      {inspoItems.length > 0 ? (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {inspoItems.map((item, idx) => {
            const url = typeof item === "string" ? item : (item?.image_url || item?.url || "");
            const id = item?.inspo_id || item?.id || `inspo_${idx}`;
            return (
              <div key={id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                {url ? <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" /> 
                  : <Image className="w-6 h-6 text-gray-300 m-auto mt-4" />}
                <button onClick={() => deleteInspo(id)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-inspo-${id}`}>
                  <X className="w-3 h-3" />
                </button>
                {item?.folder && <span className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[8px] px-1 py-0.5 text-center truncate">{item.folder}</span>}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-400 text-center py-3">No private inspo yet. Add photos to curate looks for this client.</p>
      )}
    </div>
  );
};

/**
 * PeopleTabGrid - Host Dashboard People tab with per-client preference card grid
 * Groups clients and shows all their preference cards in a grid
 * Displays dietary, accessibility, and inspo data in card thumbnails
 */
export const PeopleTabGrid = ({
  clients = [],
  rsvps = [],
  experiences = [],
  acuityClients = [],
  onViewCard,
  onCreateInvite,
  onContactClient,
  onEditClient,
  eventId
}) => {
  const t = useAdminCopy();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedClients, setExpandedClients] = useState({});
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [invitingClient, setInvitingClient] = useState(null); // client_id currently inviting
  const [acuityInviteResult, setAcuityInviteResult] = useState(null);

  // Send Acuity client a standard RSVP invite (NOT a magic link) prefilled
  // with their Acuity context (date, service, name, email).
  const sendAcuityInvite = async (clientId) => {
    if (!eventId || !clientId) return;
    setInvitingClient(clientId);
    try {
      const res = await fetch(`${API}/host/events/${eventId}/invite-acuity-client`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ client_id: clientId }),
      });
      if (res.ok) {
        const data = await res.json();
        setAcuityInviteResult(data);
        try {
          await navigator.clipboard.writeText(data.invite_url || "");
          toast.success("Invite link copied to clipboard");
        } catch {
          toast.success("Invite ready");
        }
      } else {
        toast.error("Could not generate invite");
      }
    } catch {
      toast.error("Error generating invite");
    } finally {
      setInvitingClient(null);
    }
  };

  // Group RSVPs/experiences by client to build preference cards
  const clientsWithCards = useMemo(() => {
    const clientMap = {};
    
    // Build from RSVPs (primary source for preference cards)
    (rsvps || []).forEach(rsvp => {
      const clientId = rsvp.user_id || rsvp.email || rsvp.rsvp_id;
      if (!clientMap[clientId]) {
        clientMap[clientId] = {
          user_id: clientId,
          rsvp_id: rsvp.rsvp_id,
          name: rsvp.name || `${rsvp.first_name || ''} ${rsvp.last_name || ''}`.trim() || "Guest",
          email: rsvp.email,
          phone: rsvp.phone,
          profile_image: rsvp.profile_image,
          cards: []
        };
      }
      
      // Each RSVP is essentially a preference card
      clientMap[clientId].cards.push({
        id: rsvp.rsvp_id,
        rsvp_id: rsvp.rsvp_id,
        user_id: rsvp.user_id,
        type: "rsvp",
        name: rsvp.event_name || rsvp.service_name || "Booking",
        client_name: rsvp.name || `${rsvp.first_name || ''} ${rsvp.last_name || ''}`.trim() || "Guest",
        email: rsvp.email,
        phone: rsvp.phone,
        profile_image: rsvp.profile_image,
        profile_photo_url: rsvp.profile_photo_url,
        picture: rsvp.picture,
        service_name: rsvp.service_name,
        linked_experience_name: rsvp.linked_experience_name,
        linked_event_type: rsvp.linked_event_type,
        category: rsvp.category || rsvp.label || "event",
        date: rsvp.event_date || rsvp.appointment_date || rsvp.created_at,
        appointment_date: rsvp.appointment_date,
        appointment_time: rsvp.appointment_time,
        status: rsvp.status,
        linked_products: rsvp.linked_products || [],
        // Full preferences object for detail view
        preferences: rsvp.preferences || {},
        // Defaults (dietary, accessibility are defaults)
        dietary_restrictions: rsvp.dietary_restrictions || [],
        dietary_notes: rsvp.dietary_notes,
        allergies: rsvp.allergies || [],
        accessibility_needs: rsvp.accessibility_needs || [],
        accessibility_notes: rsvp.accessibility_notes,
        invisible_disabilities: rsvp.invisible_disabilities || [],
        // Inspo
        inspo: rsvp.inspo_images || rsvp.inspo || rsvp.inspiration_images || [],
        inspo_images: rsvp.inspo_images || rsvp.inspo || rsvp.inspiration_images || [],
        vibe: rsvp.vibe,
        interests: rsvp.interests || [],
        // Sharing
        shared_defaults: rsvp.shared_defaults || [],
        share_id: rsvp.share_id,
        // Intake responses
        intake_responses: rsvp.intake_responses,
        custom_answers: rsvp.custom_answers,
        // Iter 9.48d - service-tag conditional intake responses
        // shape: { bridal: {wedding_date: '...', ...}, instructor: {...}, travel: {...} }
        tag_intake_responses: rsvp.tag_intake_responses || {},
        service_tags: rsvp.service_tags || [],
      });
    });
    
    // Also add from experiences if provided
    (experiences || []).forEach(exp => {
      const clientId = exp.user_id || exp.client_id || exp.client_email;
      if (clientId) {
        if (!clientMap[clientId]) {
          clientMap[clientId] = {
            user_id: clientId,
            name: exp.user_name || exp.client_name || "Client",
            email: exp.client_email,
            cards: []
          };
        }
        clientMap[clientId].cards.push({
          id: exp.experience_id || exp.id,
          type: "experience",
          name: exp.name || "Experience",
          category: exp.category,
          date: exp.date || exp.target_date,
          inspo: exp.inspo || exp.inspiration_images || [],
          ...exp
        });
      }
    });
    
    // Merge Acuity-imported provider_clients (no preference card yet, but
    // they have appointment context: date/service/imported_at).
    (acuityClients || []).forEach(ac => {
      const email = (ac.email || "").toLowerCase();
      const clientId = ac.client_id || email || ac.acuity_client_id;
      if (!clientId) return;
      // If we already have this client via RSVP email match, just enrich;
      // otherwise add a new entry with no cards.
      const existingKey = Object.keys(clientMap).find(k =>
        (clientMap[k].email || "").toLowerCase() === email && email
      );
      const targetKey = existingKey || clientId;
      if (!clientMap[targetKey]) {
        clientMap[targetKey] = {
          user_id: clientId,
          client_id: ac.client_id,
          name: ac.display_name || `${ac.first_name || ""} ${ac.last_name || ""}`.trim() || ac.email || "Acuity Client",
          email: ac.email,
          phone: ac.phone,
          profile_image: null,
          imported_from: ac.imported_from,
          imported_at: ac.imported_at,
          last_appointment_date: ac.last_appointment_date,
          last_service_name: ac.last_service_name,
          acuity_client_id: ac.acuity_client_id,
          rsvp_id: ac.rsvp_id || null,
          invite_sent_at: ac.invite_sent_at || null,
          cards: [],
        };
      } else {
        // Enrich existing entry with Acuity context
        clientMap[targetKey].imported_from = clientMap[targetKey].imported_from || ac.imported_from;
        clientMap[targetKey].imported_at = clientMap[targetKey].imported_at || ac.imported_at;
        clientMap[targetKey].last_appointment_date = clientMap[targetKey].last_appointment_date || ac.last_appointment_date;
        clientMap[targetKey].last_service_name = clientMap[targetKey].last_service_name || ac.last_service_name;
        clientMap[targetKey].acuity_client_id = clientMap[targetKey].acuity_client_id || ac.acuity_client_id;
        clientMap[targetKey].client_id = clientMap[targetKey].client_id || ac.client_id;
        // Backfill any cards on this client that lack service_name / date so
        // the thumbnail surfaces the Acuity service + appointment time.
        clientMap[targetKey].cards = (clientMap[targetKey].cards || []).map(c => ({
          ...c,
          service_name: c.service_name || ac.last_service_name || c.name,
          appointment_date: c.appointment_date || ac.last_appointment_date,
          date: c.date || ac.last_appointment_date,
          imported_from: c.imported_from || ac.imported_from,
        }));
      }
    });

    // Show every client (including Acuity-imported with zero cards)
    return Object.values(clientMap);
  }, [rsvps, experiences, acuityClients]);

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clientsWithCards;
    const query = searchQuery.toLowerCase();
    return clientsWithCards.filter(client => 
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
  }, [clientsWithCards, searchQuery]);

  // Toggle client expansion
  const toggleClient = (clientId) => {
    setExpandedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  // Copy share link
  const copyShareLink = async (card) => {
    const shareUrl = card.share_id 
      ? `${window.location.origin}/x/${card.share_id}`
      : `${window.location.origin}/share/card/${card.id}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      toast.success("Share link copied!");
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  // View full preference card. Only the local Dialog opens; we deliberately
  // do NOT bubble onViewCard up to the parent dashboard because it opens a
  // second modal (causing the grey-overlay double-popup bug).
  const viewFullCard = (card) => {
    setSelectedCard(card);
    setShowCardModal(true);
  };

  // Card component for preference cards - shows inspo as grid thumbnails
  // Iter 9.47.v - lightweight per-category accent palette. These match the
  // canonical color tokens stored in db.categories[*].color_primary. Until the
  // Airtable color-tokens sync ships, this is the runtime fallback.
  const CATEGORY_HEX = {
    makeup: "#E94B6E",
    hair: "#B47A4A",
    nails: "#D87BC5",
    lashes: "#7E5BAB",
    style: "#3C7BBF",
    culinary: "#D58A2E",
    wellness: "#3FA374",
    fitness: "#E15B3C",
    travel: "#2DA5C4",
    home: "#4A5F8C",
    event: "#7E5BAB",
  };

  // Tints a hex by appending an alpha. 0x14 ≈ 8% alpha for a soft wash.
  const tint = (hex, alpha = "14") => (hex && hex.startsWith("#") ? `${hex}${alpha}` : "#F5F4F2");

  const PreferenceCardThumbnail = ({ card, onClick }) => {
    const date = card.appointment_date || card.date || card.target_date;
    const hasDietary = card.dietary_restrictions?.length > 0 || card.dietary_notes;
    const hasAllergies = card.allergies?.length > 0;
    const hasAccessibility = card.accessibility_needs?.length > 0 || card.accessibility_notes || card.invisible_disabilities?.length > 0;
    const hasInspo = card.inspo?.length > 0;
    const sharedDietary = card.shared_defaults?.includes("dietary");
    const sharedAllergies = card.shared_defaults?.includes("allergies");
    const sharedAccessibility = card.shared_defaults?.includes("accessibility");
    const hasLinkedProducts = card.linked_products?.length > 0;

    // Iter 9.47.v - category accent + chip icon glyphs (no emoji per design rules)
    const catKey = (card.category || "event").toLowerCase();
    const catColor = CATEGORY_HEX[catKey] || CATEGORY_HEX.event;

    // Get inspo images (up to 4 for grid display)
    const inspoImages = (card.inspo || []).slice(0, 4).map(img => 
      typeof img === 'string' ? img : (img.image_url || img.url)
    ).filter(Boolean);
    
    return (
      <div 
        className="rounded-xl border overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
        style={{
          background: tint(catColor),
          borderColor: tint(catColor, "33"),
        }}
        onClick={() => onClick?.(card)}
        data-testid={`card-thumbnail-${card.id}`}
      >
        {/* Inspo Grid - Shows up to 4 images in a responsive grid */}
        <div className="p-3 pb-2">
          {/* Category badge & chip icons row */}
          <div className="flex items-center justify-between mb-2 gap-1">
            <Badge
              className="text-xs capitalize border-0"
              style={{ background: tint(catColor, "26"), color: catColor }}
              data-testid={`card-category-badge-${card.id}`}
            >
              {card.service_name || card.category || "experience"}
            </Badge>
            <div className="flex items-center gap-1">
              {hasAllergies && (
                <span
                  title={`Allergies: ${(card.allergies || []).join(", ") || "set"}`}
                  className="w-5 h-5 rounded-full bg-white/90 border flex items-center justify-center"
                  style={{ color: "#C2410C", borderColor: "#FDBA74" }}
                  data-testid={`card-chip-allergies-${card.id}`}
                >
                  <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.5} />
                </span>
              )}
              {hasDietary && (
                <span
                  title={`Dietary: ${(card.dietary_restrictions || []).join(", ") || "set"}`}
                  className="w-5 h-5 rounded-full bg-white/90 border flex items-center justify-center"
                  style={{ color: catColor }}
                  data-testid={`card-chip-dietary-${card.id}`}
                >
                  <Utensils className="w-2.5 h-2.5" strokeWidth={2.5} />
                </span>
              )}
              {hasAccessibility && (
                <span
                  title="Accessibility"
                  className="w-5 h-5 rounded-full bg-white/90 border flex items-center justify-center"
                  style={{ color: catColor }}
                  data-testid={`card-chip-accessibility-${card.id}`}
                >
                  <Accessibility className="w-2.5 h-2.5" strokeWidth={2.5} />
                </span>
              )}
              {hasInspo && (
                <span
                  title={`${card.inspo.length} inspo`}
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/90 border font-semibold"
                  style={{ color: catColor }}
                  data-testid={`card-chip-inspo-${card.id}`}
                >{card.inspo.length}</span>
              )}
            </div>
          </div>
          
          {/* Inspo Images Grid - Instagram style thumbnails */}
          {inspoImages.length > 0 ? (
            <div className={`grid gap-1 ${
              inspoImages.length === 1 ? 'grid-cols-1' :
              inspoImages.length === 2 ? 'grid-cols-2' :
              inspoImages.length === 3 ? 'grid-cols-3' :
              'grid-cols-4'
            }`}>
              {inspoImages.map((imgUrl, idx) => (
                <div 
                  key={idx} 
                  className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative"
                >
                  <img 
                    src={imgUrl} 
                    alt={`Inspo ${idx + 1}`} 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {/* Show +N overlay on last image if more exist */}
                  {idx === 3 && card.inspo.length > 4 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-sm font-medium">+{card.inspo.length - 4}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="h-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-purple-300" />
            </div>
          )}
        </div>
        
        {/* Info & Status */}
        <div className="px-3 pb-3">
          <p className="font-medium text-sm text-gray-900 truncate" data-testid={`card-service-${card.id}`}>
            {card.service_name || card.name || "Preference Card"}
          </p>
          {card.linked_experience_name && (
            <p className="text-[10px] text-purple-600 truncate flex items-center gap-0.5 mt-0.5">
              <Link2 className="w-2.5 h-2.5 flex-shrink-0" />
              {card.linked_experience_name}
            </p>
          )}
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-gray-500 flex items-center gap-1" data-testid={`card-date-${card.id}`}>
              <Calendar className="w-3 h-3" />
              {(() => {
                if (!date) return "TBD";
                try {
                  const d = new Date(date);
                  // Include time when present (Acuity gives full ISO).
                  const datePart = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                  const hasTime = /T\d{2}:\d{2}/.test(String(date));
                  return hasTime
                    ? `${datePart} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
                    : datePart;
                } catch (_e) { return "TBD"; }
              })()}
            </p>
            {/* Status indicator */}
            {card.status && (
              <div className={`w-2 h-2 rounded-full ${
                card.status === 'checked_in' ? 'bg-green-500' :
                card.status === 'confirmed' ? 'bg-blue-500' :
                card.status === 'pending' ? 'bg-yellow-500' :
                'bg-gray-400'
              }`} title={card.status} />
            )}
          </div>
          
          {/* Preference indicators */}
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {(hasDietary || sharedDietary) && (
              <div 
                className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center" 
                title="Has dietary preferences"
              >
                <Utensils className="w-2.5 h-2.5 text-green-600" />
              </div>
            )}
            {(hasAccessibility || sharedAccessibility) && (
              <div 
                className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center"
                title="Has accessibility needs"
              >
                <Accessibility className="w-2.5 h-2.5 text-blue-600" />
              </div>
            )}
            {card.vibe && (
              <div 
                className="w-5 h-5 rounded-full bg-pink-100 flex items-center justify-center" 
                title={`Vibe: ${card.vibe}`}
              >
                <Heart className="w-2.5 h-2.5 text-pink-600" />
              </div>
            )}
            {card.interests?.length > 0 && (
              <div 
                className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center" 
                title={`${card.interests.length} interests`}
              >
                <Star className="w-2.5 h-2.5 text-amber-600" />
              </div>
            )}
          </div>
          
          {/* Linked Products */}
          {hasLinkedProducts && (
            <div className="flex flex-wrap gap-1 mt-2">
              {card.linked_products.slice(0, 2).map((p, idx) => (
                <div key={idx} className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                  <ShoppingBag className="w-2.5 h-2.5" />
                  <span className="truncate max-w-[80px]">{p.name}</span>
                  {p.price > 0 && <span className="font-medium">${p.price}</span>}
                </div>
              ))}
              {card.linked_products.length > 2 && (
                <span className="text-[10px] text-amber-600 self-center">+{card.linked_products.length - 2}</span>
              )}
            </div>
          )}
        </div>

        {/* Iter 9.49+ R5 - removed full-card gray hover overlay. The hover
            tinted the entire card and was reported as "distracting / covering
            the whole screen" on preference cards. */}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="people-tab-grid">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Clients & Preference Cards</h2>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 ${viewMode === "grid" ? "bg-purple-100 text-purple-700" : "text-gray-500 hover:bg-gray-100"}`}
              data-testid="view-grid-btn"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 ${viewMode === "list" ? "bg-purple-100 text-purple-700" : "text-gray-500 hover:bg-gray-100"}`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <Button onClick={onCreateInvite} size="sm" data-testid="create-invite-btn">
            <Plus className="w-4 h-4 mr-1" />
            Create Invite
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search clients by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-clients"
        />
      </div>

      {/* Stats summary */}
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <span><strong>{filteredClients.length}</strong> clients</span>
        <span><strong>{filteredClients.reduce((sum, c) => sum + c.cards.length, 0)}</strong> preference cards</span>
      </div>

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="p-8 text-center">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-medium text-gray-700 mb-1">{t("host.people.empty.title", "No clients yet")}</h3>
            <p className="text-sm text-gray-500 mb-4">{t("host.people.empty.desc", "Create an invite to add your first client")}</p>
            <Button onClick={onCreateInvite} size="sm">
              <Plus className="w-4 h-4 mr-1" />
              {t("host.people.empty.cta", "Create Invite")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredClients.map(client => {
            const clientId = client.user_id || client.email;
            const isExpanded = expandedClients[clientId] !== false; // Default expanded
            
            return (
              <div key={clientId} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                {/* Client Header */}
                <button
                  onClick={() => toggleClient(clientId)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  data-testid={`client-header-${clientId}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
                      {client.profile_image ? (
                        <img src={client.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    
                    {/* Name & Info */}
                    <div className="text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">
                          {client.name || "Guest"}
                        </h3>
                        {client.imported_from === "acuity" && (
                          <Badge
                            className="bg-emerald-100 text-emerald-700 border-0 text-[10px] font-medium gap-1"
                            data-testid={`acuity-badge-${clientId}`}
                          >
                            <RefreshCw className="w-2.5 h-2.5" />
                            Acuity Sync
                            {client.imported_at && (
                              <span className="opacity-70">
                                {" · "}
                                {new Date(client.imported_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </span>
                            )}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                        {client.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {client.email}
                          </span>
                        )}
                        {client.last_appointment_date && (
                          <span className="flex items-center gap-1 text-emerald-700" data-testid={`acuity-appt-date-${clientId}`}>
                            <Calendar className="w-3 h-3" />
                            {new Date(client.last_appointment_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        )}
                        {client.last_service_name && (
                          <span className="flex items-center gap-1 text-gray-700" data-testid={`acuity-service-${clientId}`}>
                            <Sparkles className="w-3 h-3" />
                            {client.last_service_name}
                          </span>
                        )}
                        <span className="text-purple-600 font-medium">{client.cards.length} card{client.cards.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Quick actions */}
                    {onEditClient && (
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); onEditClient(client); }}
                        className="hidden sm:flex p-1.5 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
                        title="Edit profile"
                        data-testid={`edit-client-btn-${client.user_id || client.email || "row"}`}
                      >
                        <Edit3 className="w-4 h-4 text-gray-500" />
                      </span>
                    )}
                    {client.email && (
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); onContactClient?.(client); }}
                        className="hidden sm:flex p-1.5 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        <Mail className="w-4 h-4 text-gray-500" />
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </div>
                </button>

                {/* Cards Grid (Expanded) */}
                {isExpanded && (
                  <div className="p-4 bg-white space-y-4">
                    {client.cards.length > 0 ? (
                      <div className={`grid gap-4 ${viewMode === "grid" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-1"}`}>
                        {client.cards.map((card, idx) => (
                          <PreferenceCardThumbnail
                            key={card.id || idx}
                            card={card}
                            onClick={viewFullCard}
                          />
                        ))}
                      </div>
                    ) : client.imported_from === "acuity" ? (
                      <div
                        className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
                        data-testid={`acuity-empty-${clientId}`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-emerald-900">
                            No preference card yet
                          </p>
                          <p className="text-xs text-emerald-700">
                            Imported from Acuity. Send a preference invite prefilled with their booking so they can share looks, allergies, and inspo before the appointment.
                          </p>
                          {client.invite_sent_at && (
                            <p className="text-[10px] text-emerald-600 mt-1">
                              Invite already sent {new Date(client.invite_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => sendAcuityInvite(client.client_id || clientId)}
                          disabled={invitingClient === (client.client_id || clientId)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                          data-testid={`send-acuity-invite-${clientId}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" />
                          {invitingClient === (client.client_id || clientId)
                            ? "Generating..."
                            : client.invite_sent_at
                              ? "Resend invite"
                              : "Send preference invite"}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No preference cards yet. This client hasn't completed any RSVPs.
                      </p>
                    )}
                    
                    {/* Host Inspo Section */}
                    <HostInspoSection clientUserId={clientId} clientName={client.name || "Client"} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Full Preference Card Modal */}
      <Dialog open={showCardModal} onOpenChange={setShowCardModal}>        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Preference Card</DialogTitle>
            <DialogDescription>
              {selectedCard?.name || "View client preferences and inspiration"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCard && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4">
                {/* Inspo Images */}
                {selectedCard.inspo?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Inspiration ({selectedCard.inspo.length})</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedCard.inspo.slice(0, 6).map((img, idx) => {
                        const imgUrl = typeof img === 'string' ? img : img.image_url || img.url;
                        return (
                          <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                            {imgUrl && <img src={imgUrl} alt="" className="w-full h-full object-cover" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Linked Experience */}
                {selectedCard.linked_experience_name && (
                  <div className="p-3 bg-violet-50 rounded-lg">
                    <h4 className="text-sm font-medium text-violet-800 mb-1 flex items-center gap-1.5">
                      <Link2 className="w-4 h-4" /> Linked Experience
                    </h4>
                    <p className="text-sm text-violet-700">{selectedCard.linked_experience_name}</p>
                    {selectedCard.linked_event_type && (
                      <Badge variant="outline" className="text-xs bg-white mt-1 capitalize">{selectedCard.linked_event_type.replace(/_/g, ' ')}</Badge>
                    )}
                  </div>
                )}

                {/* Linked Products */}
                {selectedCard.linked_products?.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <h4 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-1.5">
                      <ShoppingBag className="w-4 h-4" /> Linked Products
                    </h4>
                    <div className="space-y-1.5">
                      {selectedCard.linked_products.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] capitalize bg-white">{p.type}</Badge>
                            <span className="text-gray-700">{p.name}</span>
                          </div>
                          {p.price > 0 && <span className="text-amber-700 font-medium">${p.price}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Dynamic preference blocks - render all shared_defaults */}
                {(selectedCard.shared_defaults || []).map(blockKey => {
                  // Get data from collected preferences or RSVP data
                  const blockData = selectedCard[blockKey] || selectedCard.preferences?.[blockKey];
                  if (!blockData || (typeof blockData === 'object' && !Array.isArray(blockData) && Object.keys(blockData).length === 0)) return null;
                  if (Array.isArray(blockData) && blockData.length === 0) return null;
                  
                  const label = blockKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  const bgColors = {
                    dietary: 'bg-green-50', accessibility: 'bg-blue-50', interests: 'bg-pink-50',
                    hair: 'bg-amber-50', face: 'bg-rose-50', lash: 'bg-violet-50', nails: 'bg-fuchsia-50',
                    wellness: 'bg-emerald-50', style: 'bg-indigo-50', travel: 'bg-sky-50'
                  };
                  const textColors = {
                    dietary: 'text-green-800', accessibility: 'text-blue-800', interests: 'text-pink-800',
                    hair: 'text-amber-800', face: 'text-rose-800', lash: 'text-violet-800', nails: 'text-fuchsia-800',
                    wellness: 'text-emerald-800', style: 'text-indigo-800', travel: 'text-sky-800'
                  };
                  
                  return (
                    <div key={blockKey} className={`p-3 ${bgColors[blockKey] || 'bg-gray-50'} rounded-lg`}>
                      <h4 className={`text-sm font-medium ${textColors[blockKey] || 'text-gray-800'} mb-2`}>{label}</h4>
                      {typeof blockData === 'object' && !Array.isArray(blockData) ? (
                        <div className="space-y-1.5">
                          {Object.entries(blockData).map(([k, v]) => {
                            if (!v || (Array.isArray(v) && v.length === 0)) return null;
                            return (
                              <div key={k}>
                                <span className="text-xs text-gray-500">{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: </span>
                                {Array.isArray(v) ? (
                                  <div className="flex flex-wrap gap-1 mt-0.5">
                                    {v.map((item, i) => <Badge key={i} variant="outline" className="text-xs bg-white">{String(item)}</Badge>)}
                                  </div>
                                ) : <span className="text-xs font-medium">{String(v)}</span>}
                              </div>
                            );
                          })}
                        </div>
                      ) : Array.isArray(blockData) ? (
                        <div className="flex flex-wrap gap-1">
                          {blockData.map((item, i) => <Badge key={i} variant="outline" className="text-xs bg-white">{String(item)}</Badge>)}
                        </div>
                      ) : <p className="text-xs">{String(blockData)}</p>}
                    </div>
                  );
                })}
                
                {/* Dynamic fallback: render all available preference data when shared_defaults absent */}
                {(!selectedCard.shared_defaults || selectedCard.shared_defaults.length === 0) && (
                  <>
                    {/* Iterate over all preference keys dynamically */}
                    {selectedCard.preferences && Object.entries(selectedCard.preferences).map(([k, v]) => {
                      if (!v || v === true || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)) return null;
                      if (Array.isArray(v) && v.length === 0) return null;
                      const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      const colorMap = {
                        dietary: { bg: 'bg-green-50', text: 'text-green-800' },
                        accessibility: { bg: 'bg-blue-50', text: 'text-blue-800' },
                        hair: { bg: 'bg-amber-50', text: 'text-amber-800' },
                        face: { bg: 'bg-rose-50', text: 'text-rose-800' },
                        nails: { bg: 'bg-fuchsia-50', text: 'text-fuchsia-800' },
                        lash: { bg: 'bg-violet-50', text: 'text-violet-800' },
                      };
                      const colors = colorMap[k] || { bg: 'bg-gray-50', text: 'text-gray-800' };
                      return (
                        <div key={k} className={`p-3 ${colors.bg} rounded-lg`}>
                          <h4 className={`text-sm font-medium ${colors.text} mb-2`}>{label}</h4>
                          {typeof v === 'object' && !Array.isArray(v) ? (
                            <div className="space-y-1.5">
                              {Object.entries(v).filter(([, val]) => val).map(([subK, subV]) => (
                                <div key={subK} className="flex items-start gap-1.5">
                                  <span className="text-xs text-gray-500 shrink-0">{subK.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</span>
                                  {Array.isArray(subV) ? (
                                    <div className="flex flex-wrap gap-1">{subV.map((item, i) => <Badge key={i} variant="outline" className="text-xs bg-white">{String(item)}</Badge>)}</div>
                                  ) : <span className="text-xs font-medium">{String(subV)}</span>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Iter 9.48d - Service-tag intake responses (bridal/instructor/travel) */}
                {selectedCard && Object.keys(selectedCard.tag_intake_responses || {}).length > 0 && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-700 mt-4 mb-2">Service-Specific Intake</h3>
                    {Object.entries(selectedCard.tag_intake_responses).map(([tag, answers]) => {
                      const tagColors = {
                        bridal: { bg: 'bg-rose-50', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700' },
                        instructor: { bg: 'bg-indigo-50', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-700' },
                        travel: { bg: 'bg-sky-50', text: 'text-sky-700', badge: 'bg-sky-100 text-sky-700' },
                      }[tag] || { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-700' };
                      return (
                        <div key={tag} className={`p-3 ${tagColors.bg} rounded-lg`} data-testid={`client-tag-intake-${tag}`}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className={`text-sm font-medium ${tagColors.text} capitalize`}>{tag}</h4>
                            <Badge className={`text-[10px] ${tagColors.badge} border-0`}>{tag}</Badge>
                          </div>
                          <div className="space-y-1.5">
                            {Object.entries(answers || {}).filter(([, v]) => v).map(([k, v]) => (
                              <div key={k} className="flex items-start gap-1.5">
                                <span className="text-xs text-gray-500 shrink-0">{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</span>
                                <span className="text-xs font-medium">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => copyShareLink(selectedCard)}
              className="flex-1"
            >
              {copiedLink ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copiedLink ? "Copied!" : "Copy Share Link"}
            </Button>
            <Button 
              onClick={() => {
                if (selectedCard?.share_id) {
                  window.open(`/x/${selectedCard.share_id}`, '_blank');
                }
              }}
              className="flex-1"
              disabled={!selectedCard?.share_id}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              View Public Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acuity invite-share-preferences result */}
      <Dialog open={!!acuityInviteResult} onOpenChange={(v) => { if (!v) setAcuityInviteResult(null); }}>
        <DialogContent className="max-w-md" data-testid="acuity-invite-result-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              Preference invite ready
            </DialogTitle>
            <DialogDescription>
              Standard RSVP link, not a magic link. Share it with your client however you like (DM, text, email).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <Input
                readOnly
                value={acuityInviteResult?.invite_url || ""}
                className="text-xs font-mono bg-white"
                data-testid="acuity-invite-url"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(acuityInviteResult?.invite_url || "");
                    toast.success("Copied");
                  } catch { /* noop */ }
                }}
                data-testid="copy-acuity-invite-btn"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy link
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => window.open(acuityInviteResult?.invite_url, "_blank")}
                data-testid="open-acuity-invite-btn"
              >
                <ExternalLink className="w-4 h-4 mr-1" /> Open
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * CreateInviteModal - Modal for hosts to create client invites
 */
export const CreateInviteModal = ({
  isOpen,
  onClose,
  onSave,
  adminDefaults = {},
  pilotType = "makeup"
}) => {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [prefillData, setPrefillData] = useState({});
  const [overrideDefaults, setOverrideDefaults] = useState(false);

  const handleSave = () => {
    if (!clientEmail.trim()) {
      return;
    }
    onSave?.({
      name: clientName,
      email: clientEmail,
      phone: clientPhone,
      prefill_data: prefillData,
      override_admin_defaults: overrideDefaults
    });
    // Reset
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setPrefillData({});
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Client Invite</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Client Name</label>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Email *</label>
            <Input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="client@email.com"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Phone (optional)</label>
            <Input
              type="tel"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
          
          {/* Override admin defaults option */}
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
            <input
              type="checkbox"
              id="override-defaults"
              checked={overrideDefaults}
              onChange={(e) => setOverrideDefaults(e.target.checked)}
              className="rounded border-amber-300"
            />
            <label htmlFor="override-defaults" className="text-sm text-amber-800">
              Override admin presets for this invite
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={!clientEmail.trim()}>
              Create Invite
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PeopleTabGrid;
